import { useTableActionContext } from '@aibees/elephant/context';
import { TemplateManagement } from '@aibees/shared/components/template-management';
import { ParsedPreset } from '@aibees/shared/definition';
import { OperationMode } from '@aibees/shared/features';
import { classnames } from '@aibees/shared/utils';
import { memo, useMemo } from 'react';
import {
  DEFAULT_EXPORT_TEMPLATE_ID,
  EXPORT_TEMPLATE_LABEL_WIDTH,
  EXPORT_TEMPLATE_LABEL_WITH_VISIBILITY_ICON_WIDTH,
} from '../../constants';
import { useTemplateContext } from '../../context/TemplateContext';
import { useExportTemplateDataType } from '../../hooks/use-export-template-data-type';
import type { ExportTemplatesSelectProps as Props } from './types';

export const ExportTemplateSelect = memo(
  ({ className, icon: _icon, firstState }: Props) => {
    const {
      selectedTemplate,
      templates,
      selectedPreset,
      setSelectedTemplate,
      setTemplateName,
      setTemplateMode,
    } = useTemplateContext();
    const { templateDialogState } = useTableActionContext();
    const exportTemplateDataType = useExportTemplateDataType();

    const handleClickOpen = () => {
      firstState?.onFalse();
      templateDialogState.onTrue();
    };

    const handleEdit = (item: ParsedPreset<any>) => {
      firstState?.onFalse();
      templateDialogState?.onTrue?.();
      setTemplateName(item.name);
      setSelectedTemplate(item);
      setTemplateMode(OperationMode.EDIT);
    };
    const selectedTemplateId = useMemo(() => {
      const resolved = selectedTemplate?.id ?? selectedPreset?.value;
      // Fall back to the (synthetic) default when nothing is selected or the
      // persisted selection points at a template that no longer exists — e.g. a
      // legacy backend "Default Export Template" preset that is no longer listed.
      const exists = resolved && templates.some((t) => t.id === resolved);
      return exists ? resolved : DEFAULT_EXPORT_TEMPLATE_ID;
    }, [selectedTemplate, selectedPreset, templates]);

    return (
      <TemplateManagement
        className={classnames('ExportTemplateSelect', className)}
        presetType={exportTemplateDataType}
        templates={templates}
        placeholder="Search to select a template for export"
        selectedPreset={selectedPreset}
        selectedTemplateId={selectedTemplateId}
        deleteDialogTitle="Delete CSV Template"
        deleteNotificationTitle="Export Template Removed"
        deleteNotificationDescription="The selected export template has been deleted from your workspace."
        deleteNotificationIcon="file-csv-outline"
        labelWidth={EXPORT_TEMPLATE_LABEL_WIDTH}
        labelWidthWithVisibilityIcon={
          EXPORT_TEMPLATE_LABEL_WITH_VISIBILITY_ICON_WIDTH
        }
        onCreate={handleClickOpen}
        onSelect={setSelectedTemplate}
        onEdit={handleEdit}
      />
    );
  }
);

ExportTemplateSelect.displayName = 'ExportTemplateSelect';
